/**
 * Aspect — Baked lighting runtime (hybrid GI pipeline)
 * --------------------------------------------------------------
 * Runtime half of the Blender bake produced by tools/bake/bake_room.py.
 * Implements the Moka-style hybrid architecture:
 *
 *   - Static shell (walls/floor/ceiling): baked day+night lightmaps on a
 *     dedicated UV2 shell GLB, time-blended in the shader. The realtime sun
 *     keeps casting shadowmaps on top (direct sun is EXCLUDED from the bake).
 *   - Reflections: two prefiltered environment atlases (day/night) built from
 *     baked interior cubemaps, box-projected to the room bounds and blended
 *     by the same global timeline uniform.
 *   - Dynamic furniture: SH L2 probe grid (see probe_field.js).
 *   - Streaming: low-res lightmaps load first for instant feedback; 2K maps,
 *     cubemaps, and probes stream in behind and swap seamlessly.
 *   - Fallback: no manifest / bounds mismatch / live plan edits → the room
 *     stays on (or reverts to) the existing realtime lighting path.
 *
 * One global timeline variable (0.0 = day, 1.0 = night) drives every blend:
 * the `uTimeBlend` device-scope uniform, probe re-blending, and the post LUT.
 */

import * as pc from "playcanvas";
import { hourToSunElevation } from "../day_night_cycle.js";
import { applyShellChunks, applyFurnitureChunks, removeTimeBlendChunks } from "./time_blend_chunks.js";
import { ProbeField } from "./probe_field.js";
import { installSeamSeals } from "../seam_seals.js";
import { placeAmbientShadowLight } from "../environment.js";

const BAKED_BASE = "./assets/baked";
const BOUNDS_EPS = 0.01;

const clamp01 = (v) => Math.max(0, Math.min(1, v));

function smoothstep(edge0, edge1, x) {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** Map the 24h clock onto the day↔night blend (0 = full day, 1 = full night). */
export function hourToTimeBlend(hour) {
  return 1 - smoothstep(-8, 20, hourToSunElevation(hour));
}

/** Load a PNG as an RGBM texture asset (mipmapped, repeat off). */
function loadRgbmTexture(app, url, name) {
  return new Promise((resolve, reject) => {
    // filename keeps parser detection working when the url carries ?v= cache-busting
    const filename = url.split("?")[0].split("/").pop();
    const asset = new pc.Asset(name, "texture", { url, filename }, { type: pc.TEXTURETYPE_RGBM, mipmaps: true });
    asset.once("load", () => {
      const tex = asset.resource;
      tex.type = pc.TEXTURETYPE_RGBM;
      tex.addressU = pc.ADDRESS_CLAMP_TO_EDGE;
      tex.addressV = pc.ADDRESS_CLAMP_TO_EDGE;
      tex.anisotropy = 4;
      resolve(tex);
    });
    asset.once("error", (err) => reject(err ?? new Error(`lightmap load failed: ${url}`)));
    app.assets.add(asset);
    app.assets.load(asset);
  });
}

/** Load an equirect .hdr and prefilter it into a PlayCanvas env atlas. */
function loadEnvAtlas(app, url, name) {
  return new Promise((resolve, reject) => {
    const filename = url.split("?")[0].split("/").pop();
    const asset = new pc.Asset(name, "texture", { url, filename }, {
      type: pc.TEXTURETYPE_RGBE,
      addressu: "clamp",
      addressv: "clamp",
    });
    asset.once("load", () => {
      const equirect = asset.resource;
      equirect.projection = pc.TEXTUREPROJECTION_EQUIRECT;
      const lighting = pc.EnvLighting.generateLightingSource(equirect, { size: 128 });
      const atlas = pc.EnvLighting.generateAtlas(lighting, {
        size: 512,
        numReflectionSamples: 1024,
        numAmbientSamples: 2048,
      });
      lighting.destroy();
      resolve(atlas);
    });
    asset.once("error", (err) => reject(err ?? new Error(`cubemap load failed: ${url}`)));
    app.assets.add(asset);
    app.assets.load(asset);
  });
}

/** Structural fingerprint — changes only when walls/openings actually change. */
function roomSignature(room) {
  return JSON.stringify([room?.walls ?? null, room?.openings ?? null, room?.bounds ?? null]);
}

/** Load a GLB container asset. */
function loadContainer(app, url, name) {
  return new Promise((resolve, reject) => {
    app.assets.loadFromUrlAndFilename(url, `${name}.glb`, "container", (err, asset) => {
      if (err || !asset?.resource) reject(err ?? new Error(`shell load failed: ${url}`));
      else resolve(asset);
    });
  });
}

export class BakedLighting {
  /**
   * @param {pc.AppBase} app
   * @param {{ scene: import('../../scene_manager.js').SceneManager,
   *           lightingRoot: pc.Entity }} opts
   */
  constructor(app, { scene, lightingRoot }) {
    this.app = app;
    this.scene = scene;
    this.lightingRoot = lightingRoot;

    this.active = false;
    this.manifest = null;
    this.shellEntity = null;
    this.probeField = new ProbeField(app, scene);
    this._timeBlend = 0;
    this._blendId = app.graphicsDevice.scope.resolve("uTimeBlend");
    this._blendId.setValue(0);
    this._boundMaterials = new Set();
    this._furnitureMaterials = new Set();
    this._hiddenInstances = [];
    this._materialSwaps = [];   // placeholder-storey meshes: [{ mi, original }]
    this._swapClones = new Map(); // original material -> lightmap-free clone
    /** @type {{ mi: pc.MeshInstance, original: pc.StandardMaterial, clone: pc.StandardMaterial, entity: pc.Entity }[]} */
    this._wetFloorSwaps = [];
    this._atlases = { day: null, night: null };
    this._generation = 0;

    this._roomSignature = null;
    this._onRoomLoaded = (room) => void this._activateForRoom(room);
    // Wall meshes are rebuilt both for cosmetic upgrades (door/window GLBs
    // streaming in) and for real plan edits. Cosmetic rebuilds keep the same
    // wall/opening data — reactivate over the fresh meshes; real edits change
    // the data and fall back to realtime lighting (bake is stale).
    this._onGeometryChanged = () => {
      const room = this.scene.room;
      if (this._roomSignature && room && this._roomSignature === roomSignature(room)) {
        void this._activateForRoom(room);
      } else {
        this.deactivate("plan edit — reverting to realtime lighting");
      }
    };
    this._onTimeChanged = ({ hour }) => this.setTimeline(hourToTimeBlend(hour));
    this._onPlacementAdded = (p) => this.applyToFurnitureEntity(this.scene.entities.get(p?.id));
    app.on("scene:room-loaded", this._onRoomLoaded);
    app.on("scene:geometry-changed", this._onGeometryChanged);
    app.on("time:changed", this._onTimeChanged);
    app.on("scene:placement-added", this._onPlacementAdded);
  }

  /** Single global timeline variable: 0.0 = day … 1.0 = night. */
  setTimeline(t) {
    this._timeBlend = clamp01(t);
    this._blendId.setValue(this._timeBlend);
    this.probeField.setTimeBlend(this._timeBlend);
    this.app.renderNextFrame = true;
  }

  get timeline() {
    return this._timeBlend;
  }

  // ------------------------------------------------------------------
  // Activation
  // ------------------------------------------------------------------

  async _activateForRoom(room) {
    const gen = ++this._generation;
    if (!room?.id || !room?.bounds) return;
    // Cleared floor-only rooms no longer match the baked wall geometry.
    if (!room.walls?.length) return;
    // Safari: extra shell GLB + lightmaps during boot is what kills the tab.
    // Realtime lights already match the desktop look closely enough on phones.
    if (typeof document !== "undefined" && document.body?.classList.contains("touch-ui")) {
      return;
    }

    const base = `${BAKED_BASE}/${String(room.id).toLowerCase()}`;
    let manifest;
    try {
      const res = await fetch(`${base}/manifest.json`, { cache: "no-cache" });
      if (!res.ok) return; // no bake for this map — realtime fallback
      manifest = await res.json();
    } catch {
      return;
    }
    if (gen !== this._generation) return;
    if (!this._boundsMatch(room.bounds, manifest.bounds)) {
      console.info("[baked] manifest bounds mismatch — realtime fallback");
      return;
    }
    // The bake is tied to one storey (floorY is baked into the shell GLB).
    const ceilH = room.dimensions?.height ?? 2.8;
    const floorY = room.floorY ?? ((room.activeFloor ?? 1) - 1) * ceilH;
    if (Math.abs(floorY - (manifest.floorY ?? 0)) > BOUNDS_EPS) {
      console.info("[baked] storey mismatch — realtime fallback");
      return;
    }

    const surf = this.scene.roomRoot?._aspectSurf;
    if (!surf) return;

    // Cache-bust every asset against the manifest's bake timestamp: the dev
    // server doesn't hot-reload public/ and browsers disk-cache the PNGs, so
    // without this a rebake keeps showing stale lightmaps after reload.
    const bust = encodeURIComponent(manifest.generated ?? manifest.mapHash ?? "");
    const url = (name) => `${base}/${name}${bust ? `?v=${bust}` : ""}`;

    try {
      // --- Stage 1 (fast): shell GLB + low-res lightmaps ------------------
      const [shellAsset, lmDayLow, lmNightLow] = await Promise.all([
        loadContainer(this.app, url(manifest.shell), `baked-shell-${manifest.map}`),
        loadRgbmTexture(this.app, url(manifest.lightmaps.day.low), "lm-day-low"),
        loadRgbmTexture(this.app, url(manifest.lightmaps.night.low), "lm-night-low"),
      ]);
      if (gen !== this._generation || !this.scene.roomRoot?._aspectSurf) return;

      // Swap only after assets are in — tearing down first left interiors
      // black whenever this call aborted or a newer activate raced it.
      this.deactivate();
      const liveSurf = this.scene.roomRoot?._aspectSurf;
      if (gen !== this._generation || !liveSurf) {
        this._tuneRealtimeLights(false);
        return;
      }

      this.manifest = manifest;
      this._installShell(shellAsset, liveSurf);
      this._bindLightmaps(liveSurf, lmDayLow, lmNightLow);
      // Seals before hide so _hideProceduralShell can strip lightmaps from them
      // (they share wall/ceiling mats but have no UV2).
      installSeamSeals(this.scene.roomRoot, room, liveSurf);
      this._hideProceduralShell();
      this._bindWetFloorOverlays(liveSurf);
      this._tuneRealtimeLights(true);
      placeAmbientShadowLight(this.lightingRoot, room);
      this._roomSignature = roomSignature(room);
      this.active = true;
      this.setTimeline(this._timeBlend);
      console.info(`[baked] active for map "${manifest.map}" (low-res stage)`);

      // --- Stage 2 (streamed): 2K lightmaps, cubemaps, probes -------------
      // Safari: skip the cubemap prefilter (1024 samples) and 2K maps — low-res
      // GI is already on the shell and is the most the tab can hold.
      if (typeof document !== "undefined" && document.body?.classList.contains("touch-ui")) {
        return;
      }
      void this._streamHighRes(url, manifest, liveSurf, gen);
    } catch (err) {
      console.warn("[baked] activation failed — realtime fallback:", err);
      this.deactivate();
    }
  }

  async _streamHighRes(url, manifest, surf, gen) {
    const tasks = [
      loadRgbmTexture(this.app, url(manifest.lightmaps.day.full), "lm-day")
        .then((day) => loadRgbmTexture(this.app, url(manifest.lightmaps.night.full), "lm-night")
          .then((night) => {
            if (gen !== this._generation || !this.active) return;
            this._bindLightmaps(surf, day, night);
            console.info("[baked] 2K lightmaps streamed in");
          })),
      Promise.all([
        loadEnvAtlas(this.app, url(manifest.cubemaps.day), "env-day"),
        loadEnvAtlas(this.app, url(manifest.cubemaps.night), "env-night"),
      ]).then(([day, night]) => {
        if (gen !== this._generation || !this.active) return;
        this._atlases = { day, night };
        this._bindReflections(surf, manifest);
        console.info("[baked] box-projected reflection atlases ready");
      }),
    ];
    if (manifest.probes) {
      tasks.push(
        this.probeField
          .load(url(manifest.probes.day), url(manifest.probes.night))
          .then(() => {
            if (gen !== this._generation || !this.active) return;
            this.probeField.activate();
            for (const { entity } of this._wetFloorSwaps) {
              if (entity) this.probeField.trackEntity(entity, `wet:${entity.name}`);
            }
            console.info("[baked] SH L2 probe grid active");
          }),
      );
    }
    await Promise.allSettled(tasks);
    this.app.renderNextFrame = true;
  }

  _boundsMatch(a, b) {
    if (!a || !b) return false;
    for (const k of ["min", "max"]) {
      for (let i = 0; i < 3; i++) {
        if (Math.abs((a[k]?.[i] ?? 0) - (b[k]?.[i] ?? 0)) > BOUNDS_EPS) return false;
      }
    }
    return true;
  }

  /** Instantiate the UV2 shell and re-point its meshes at the room's shared materials. */
  _installShell(shellAsset, surf) {
    const shell = shellAsset.resource.instantiateRenderEntity();
    shell.name = "baked-shell";
    const byRole = {
      shell_wall: surf.wall,
      shell_floor: surf.floorWood,
      shell_ceiling: surf.ceiling,
    };
    for (const render of shell.findComponents("render")) {
      for (const mi of render.meshInstances) {
        const role = mi.material?.name;
        if (byRole[role]) mi.material = byRole[role];
        // Walls AND ceiling block the realtime sun so direct light can only
        // enter through window openings (no roof leak). The floor stays a
        // pure receiver so furniture keeps crisp shadows on top of the GI.
        mi.castShadow = role !== "shell_floor";
        mi.receiveShadow = true;
      }
    }
    this.scene.roomRoot.addChild(shell);
    this.shellEntity = shell;
  }

  /** Attach (or swap) day/night lightmaps on the shared shell materials. */
  _bindLightmaps(surf, dayTex, nightTex) {
    for (const mat of [surf.wall, surf.floorWood, surf.ceiling]) {
      if (!mat) continue;
      mat.lightMap = dayTex;
      mat.lightMapUv = 1;
      mat.setParameter("texture_lightMapNight", nightTex);
      applyShellChunks(mat);
      mat.update();
      this._boundMaterials.add(mat);
    }
  }

  /** Box-projected, time-blended IBL on shell + already-spawned furniture. */
  _bindReflections(surf, manifest) {
    const box = this._projectionBox(manifest);
    for (const mat of this._boundMaterials) {
      this._applyReflection(mat, box, /* shell */ true);
    }
    // Furniture spawned before the atlases streamed in.
    for (const entity of this.scene.entities.values()) {
      this.applyToFurnitureEntity(entity);
    }
    this.app.renderNextFrame = true;
  }

  _projectionBox(manifest) {
    const { min, max } = manifest.bounds;
    const center = new pc.Vec3((min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2);
    const half = new pc.Vec3((max[0] - min[0]) / 2, (max[1] - min[1]) / 2, (max[2] - min[2]) / 2);
    return new pc.BoundingBox(center, half);
  }

  _applyReflection(mat, box, shell = false) {
    if (!this._atlases.day || !mat) return;
    mat.envAtlas = this._atlases.day;
    mat.setParameter("texture_envAtlasNight", this._atlases.night);
    mat.cubeMapProjection = pc.CUBEPROJ_BOX;
    mat.cubeMapProjectionBox = box;
    if (shell) applyShellChunks(mat);
    else applyFurnitureChunks(mat);
    mat.update();
  }

  /**
   * Give a furniture entity the baked look: box-projected blended reflections
   * on its materials and (via ProbeField) per-instance SH ambient. Safe to
   * call repeatedly; opaque materials only (glass keeps its tuned look).
   */
  applyToFurnitureEntity(entity) {
    if (!this.active || !this._atlases.day || !entity || !this.manifest) return;
    const box = this._projectionBox(this.manifest);
    for (const render of entity.findComponents("render")) {
      for (const mi of render.meshInstances ?? []) {
        const mat = mi.material;
        if (!mat || this._furnitureMaterials.has(mat) || this._boundMaterials.has(mat)) continue;
        if (mat.blendType != null && mat.blendType !== pc.BLEND_NONE) continue;
        this._applyReflection(mat, box, false);
        this._furnitureMaterials.add(mat);
      }
    }
  }

  /**
   * Wet-room / kitchen tile overlays have no UV2 lightmap. Keep realtime lighting
   * so window sun still brightens kitchens like living-room wood floors, and bind
   * probe SH ambient (same as furniture) so windowless bathrooms stay dark instead
   * of washing out from flat scene ambient.
   */
  _bindWetFloorOverlays(surf) {
    this._restoreWetFloorOverlays();
    const root = this.scene.roomRoot;
    if (!root) return;

    const visit = (e) => {
      if (String(e.name ?? "").startsWith("floor-tile-") && e.render) {
        for (const mi of e.render.meshInstances ?? []) {
          const original = mi.material;
          if (!original) continue;
          const clone = original.clone();
          clone.name = `${original.name || "floorTile"}-baked-gi`;
          clone.useSkybox = false;
          clone.useLighting = true;
          clone.emissive = new pc.Color(0, 0, 0);
          clone.emissiveIntensity = 0;
          clone.update();
          mi.material = clone;
          mi.receiveShadow = true;
          mi.castShadow = false;
          this._wetFloorSwaps.push({ mi, original, clone, entity: e });
        }
        // Probe SH replaces flat ambient — dark bathrooms, sunlit kitchens OK.
        if (this.probeField.active) {
          this.probeField.trackEntity(e, `wet:${e.name}`);
        }
      }
      for (const c of e.children ?? []) visit(c);
    };
    visit(root);
  }

  _restoreWetFloorOverlays() {
    for (const { mi, original, clone } of this._wetFloorSwaps) {
      if (mi && original && mi.material === clone) mi.material = original;
      clone?.destroy?.();
    }
    this._wetFloorSwaps = [];
  }

  /**
   * Hide the realtime plaster/floor/ceiling boxes the GLB shell replaces.
   * Restricted to the baked storey's Y range so multi-storey placeholder
   * shells and the building roof stay visible.
   */
  _hideProceduralShell() {
    this._hiddenInstances = [];
    const root = this.scene.roomRoot;
    if (!root || !this.manifest) return;
    const yLo = (this.manifest.floorY ?? 0) - 0.2;
    const yHi = (this.manifest.floorY ?? 0) + (this.manifest.ceilH ?? 2.8) + 0.2;
    const KINDS = new Set(["wall", "floor", "ceiling"]);
    const visit = (e) => {
      if (e !== this.shellEntity) {
        // Wet-room tile overlays stay visible (the bake shell is plain wood).
        const isOverlay = String(e.name ?? "").startsWith("floor-tile-");
        const isSeal = e._aspectSeamSeal || String(e.name ?? "").startsWith("seam-seal-");
        const y = e.getPosition?.().y ?? 0;
        if (KINDS.has(e._aspectKind) && e.render && !isOverlay && y >= yLo && y <= yHi) {
          for (const mi of e.render.meshInstances ?? []) {
            if (mi.visible) {
              mi.visible = false;
              this._hiddenInstances.push(mi);
            }
          }
        } else if (e.render) {
          // Meshes outside the baked storey (placeholder shells, roof) and seam
          // seals share shell materials but have no UV2 — swap in lightmap-free clones.
          this._swapOffLightmap(e);
          // Seals must keep casting even though they are not "wall" kind.
          if (isSeal) {
            for (const mi of e.render.meshInstances ?? []) {
              mi.castShadow = true;
              mi.receiveShadow = true;
              mi.visible = true;
            }
          }
        }
        for (const c of e.children) visit(c);
      }
    };
    visit(root);
  }

  _swapOffLightmap(entity) {
    for (const mi of entity.render?.meshInstances ?? []) {
      const mat = mi.material;
      if (!this._boundMaterials.has(mat)) continue;
      let clone = this._swapClones.get(mat);
      if (!clone) {
        clone = mat.clone();
        clone.lightMap = null;
        clone.envAtlas = null;
        clone.cubeMapProjection = pc.CUBEPROJ_NONE;
        removeTimeBlendChunks(clone);
        clone.update();
        this._swapClones.set(mat, clone);
      }
      this._materialSwaps.push({ mi, original: mat });
      mi.material = clone;
    }
  }

  /**
   * Hybrid split: sun + bounce fills + ambient-shadow stay realtime (shadowmaps
   * over the baked GI). Fills used to be switched off so only lightmaps lit the
   * room — that crushed interiors to black when the bake raced or a room had
   * little window sun. The sun's shadowIntensity stays at 1.0 so direct sun
   * does not bleed through the ceiling.
   */
  _tuneRealtimeLights(baked) {
    for (const name of ["fill", "top-fill"]) {
      const e = this.lightingRoot?.findByName(name);
      if (e) e.enabled = true;
    }
    const sun = this.lightingRoot?.findByName("sun");
    if (sun?.light) {
      sun.light.shadowIntensity = 1.0;
      sun.light.affectDynamic = true;
      sun.light.affectLightmapped = true;
    }
    // Soft overhead caster stays on in baked mode so furniture still grounds
    // outside the window sun patch (mesh shadows update when items move).
    const amb = this.lightingRoot?.findByName("ambient-shadow");
    if (amb) {
      amb.enabled = true;
      if (amb.light) {
        amb.light.intensity = baked ? 0.45 : 0.5;
        amb.light.shadowIntensity = 1.0;
        amb.light.shadowBias = 0.06;
        amb.light.normalOffsetBias = 0.12;
        // Floor only — never light furniture (avoids stripe acne on cushions).
        amb.light.affectDynamic = false;
        amb.light.affectLightmapped = true;
      }
    }
  }

  // ------------------------------------------------------------------
  // Deactivation / fallback
  // ------------------------------------------------------------------

  deactivate(reason = null) {
    this._tuneRealtimeLights(false);
    if (!this.active && !this.shellEntity && !this._wetFloorSwaps.length) {
      if (reason) console.info(`[baked] deactivated: ${reason}`);
      return;
    }
    if (reason) console.info(`[baked] deactivated: ${reason}`);

    this._restoreWetFloorOverlays();

    for (const mi of this._hiddenInstances) mi.visible = true;
    this._hiddenInstances = [];

    for (const { mi, original } of this._materialSwaps) {
      if (mi.material !== original) mi.material = original;
    }
    this._materialSwaps = [];
    for (const clone of this._swapClones.values()) clone.destroy();
    this._swapClones.clear();

    for (const mat of this._boundMaterials) {
      mat.lightMap = null;
      mat.envAtlas = null;
      mat.cubeMapProjection = pc.CUBEPROJ_NONE;
      mat.deleteParameter?.("texture_lightMapNight");
      mat.deleteParameter?.("texture_envAtlasNight");
      removeTimeBlendChunks(mat);
      mat.update();
    }
    this._boundMaterials.clear();

    for (const mat of this._furnitureMaterials) {
      mat.envAtlas = null;
      mat.cubeMapProjection = pc.CUBEPROJ_NONE;
      mat.deleteParameter?.("texture_envAtlasNight");
      removeTimeBlendChunks(mat);
      mat.update();
    }
    this._furnitureMaterials.clear();

    this.probeField.deactivate();
    this._tuneRealtimeLights(false);

    this.shellEntity?.destroy();
    this.shellEntity = null;
    this.manifest = null;
    this._roomSignature = null;
    this.active = false;
    this.app.renderNextFrame = true;
  }

  destroy() {
    this.deactivate();
    this.probeField.destroy();
    this.app.off("scene:room-loaded", this._onRoomLoaded);
    this.app.off("scene:geometry-changed", this._onGeometryChanged);
    this.app.off("time:changed", this._onTimeChanged);
    this.app.off("scene:placement-added", this._onPlacementAdded);
  }
}
